<template>
  <div class="recipe d-flex flex-column">
    <div class="page container">
      <div class="row">
        <div class="col-md-12 col-lg-5">
          <MainImagePreview :img-link="recipePreviewImage" class="container" />
        </div>

        <div class="col-md-12 col-lg-7">
          <h1 class="custom-title mb-3 mt-5">{{ $get(recipe, 'name') }}</h1>
          <div
            class="recipe__author mb-4 d-flex justify-content-between flex-wrap"
          >
            <span>
              by
              <NuxtLink
                :to="localePath(getLinkToCreator($get(recipe, 'creator')))"
              >
                @{{ $get(recipe, 'creator.profile.handle') }}
              </NuxtLink>
            </span>
          </div>
          <div
            v-if="$get(recipe, 'description')"
            class="recipe__description mt-3 mb-4 line-height-22"
          >
            <ViewMore
              :text="$get(recipe, 'description')"
              :lines="4"
              :line-height="22"
            />
          </div>
          <div v-if="recipeYield" class="recipe__yield mt-3">
            <img
              class="recipe__icon float-left mr-2"
              src="@/assets/icons/knife-fork.svg"
              alt="knife and fork"
            />
            {{ recipeYield }}
          </div>
          <div v-if="recipeTime" class="recipe__time mt-4">
            <img
              class="recipe__icon float-left mr-2"
              src="@/assets/icons/time.svg"
              alt="time"
            />
            {{ recipeTime }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="product" class="container mt-3 mt-md-5">
      <b-row>
        <b-col v-if="recipeIngredients.length" md="6">
          <h3 class="mb-3">Ingredients</h3>
          <div class="mb-2">
            Here are the basic ingredients that you will need to make
            {{ $get(recipe, 'name') }}:
          </div>
          <div
            v-for="(ingerdient, index) in recipeIngredients"
            :key="`ingredient-${index}`"
            class="recipe__ingredient py-3"
          >
            {{ $get(ingerdient, 'name') || $get(ingerdient, 'text') }}
          </div>
        </b-col>
        <b-col v-if="recipeMethods.length" md="6">
          <h3 class="mb-3 mt-md-0 mt-5">Method</h3>
          <div class="mb-2">To make {{ $get(recipe, 'name') }}, simply…</div>
          <div
            v-for="(method, index) in recipeMethods"
            :key="`method-${index}`"
            class="recipe__method py-2"
          >
            <span class="recipe__method-order mr-2">{{ index + 1 }}</span>
            {{ $get(method, 'text') }}
          </div>

          <div v-if="recipeLinks" class="recipe__links my-5">
            <h4 class="recipe__links-title mb-4">Recipe Links</h4>

            <div
              v-for="(link, description) in recipeLinks"
              :key="description"
              class="recipe__links-item mb-3"
            >
              <span>{{ description || 'View recipe here' }}</span>
              <span class="ml-auto">
                <img
                  v-for="platform in link"
                  :key="platform.url"
                  class="ml-3 cursor-pointer"
                  width="16"
                  :src="getImageItem(platform)"
                  :alt="platform.url"
                  @click.stop="handleSocialIconClick(platform.url)"
                />
              </span>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row v-if="product" class="mt-5">
        <b-col md="5">
          <ProductImages :images="previewImages" :is-pre-order="isPreOrder" />
        </b-col>
        <b-col md="7">
          <PurchaseProduct
            is-recipe-page
            :product="product"
            :questions="questions"
            @onQuantityUpdate="setQuantity"
          />
        </b-col>
      </b-row>

      <ProductBundlePanel
        v-if="bundleProduct"
        :products="[product, bundleProduct]"
        @click="handleProductBundleClick"
      >
        <template #title>
          <h4>Frequently bought together</h4>
        </template>
      </ProductBundlePanel>
    </div>

    <div class="container">
      <div v-if="recommendedRecipes && recommendedRecipes.length">
        <h2 class="mt-5">Related recipes</h2>
        <RecipeGrid
          :type="viewType.CUSTOM"
          :count-items="6"
          :recipes="recommendedRecipes"
          @click="handleRecipeClick"
        />
      </div>

      <AboutCreator class="my-4" :creator="creator" />

      <Carousel
        v-if="products.length"
        id="contributed-products"
        class="my-5"
        :items="products"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductBundleClick"
      >
        <template #title>
          <h2>Also featuring {{ $get(creator, 'profile.brandName') }}</h2>
        </template>
      </Carousel>

      <CreatorCarousel
        id="creators"
        class="mt-4"
        :items="creators"
        @click="handleCreatorClick"
      >
        <template #title>
          <h2>Discover creators</h2>
        </template>
      </CreatorCarousel>

      <TrendingBlock v-if="trendingFeed" class="mt-5" :items="trendingFeed" />
    </div>
  </div>
</template>

<script>
import {
  isPlainObject,
  flatten,
  isEmpty,
  get,
  inRange,
  groupBy,
  includes
} from 'lodash';
import { mapGetters, mapState } from 'vuex';

import PurchaseProduct from '@/components/PurchaseProduct';
import MainImagePreview from '@/components/MainImagePreview';
import ViewMore from '@/components/ViewMore';
import ProductImages from '@/components/ProductImages';
import ProductBundlePanel from '@/components/ProductBundlePanel';
import AboutCreator from '@/components/AboutCreator';

import { fetchCreatorsTrending } from '@/services/statistics';
import { fetchProductConfig } from '@/services/config';
import { fetchOneRecipe } from '@/services/recipes';
import {
  fetchRecipesForOneProduct,
  fetchProductsRecommended
} from '@/services/products';
import { fetchTrendingFeed } from '@/services/selections';
import {
  CarouselModes,
  FallbackImageURL,
  ProductAvailability,
  SupportedCreatorPlatforms
} from '@/misc/constants';
import getPreviewImages from '@/misc/getPreviewImages';
import { viewType } from '@/components/RecipeGrid/componentTypes';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import formatMoney from '@/misc/formatMoney';
import getFormatPrice from '@/misc/getFormatPrice';
import filterProductsByRegion from '@/misc/filterProductByRegion';
import { productAvailability } from '@/checkoutForm/lib/constants';

export default {
  name: 'OneRecipe',

  components: {
    PurchaseProduct,
    MainImagePreview,
    ViewMore,
    ProductImages,
    ProductBundlePanel,
    AboutCreator,
    TrendingBlock: () =>
      import(/* webpackPrefetch: true */ '@/components/TrendingBlock/Index'),
    RecipeGrid: () =>
      import(/* webpackPrefetch: true */ '@/components/RecipeGrid/Index'),
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index'),
    CreatorCarousel: () =>
      import(/* webpackPrefetch: true */ '@/components/CreatorCarousel')
  },

  asyncData({ $axios, route, error }) {
    const query = {
      query: {
        $populate: [{ path: 'creator' }, { path: 'product' }]
      }
    };
    return fetchOneRecipe($axios, route.params?.id, query)
      .then((recipe) => {
        return {
          recipe,
          product: recipe?.originalProduct,
          creator: recipe?.creator
        };
      })
      .catch((err) => {
        const errorCode = get(err, 'response.data.code');
        error({ statusCode: inRange(errorCode, 400, 405) ? 404 : 500 });
        return { recipe: {}, product: null, creator: null };
      });
  },

  data() {
    return {
      CarouselModes,
      viewType,
      trendingFeed: null,
      recommendedRecipes: [],
      creators: [],
      questions: [],
      bundleProduct: null,
      quantity: 1
    };
  },

  head() {
    return {
      title: `${this.recipe?.name} - ${this.product?.name} - ${this.creator?.profile?.handle} | Found it!`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Found it! - This recipe, ${this.recipe?.name},  is included in the ${this.product?.name} from creator @${this.creator?.profile?.handle}\nDiscover more books and recipes at Found.us`
        }
      ]
    };
  },

  computed: {
    ...mapState('main', ['menuVisible', 'mobileKeyboardVisible']),
    ...mapState('config', ['config']),
    ...mapGetters({
      currency: 'config/currency',
      region: 'config/region'
    }),
    products() {
      return filterProductsByRegion(
        this.$get(this.recipe, 'contributedProducts', []),
        this.region
      );
    },
    recipePreviewImage() {
      return (
        this.$get(this.recipe, 'images[0].fileURL') ||
        this.$get(this.recipe, 'images[0].url') ||
        FallbackImageURL
      );
    },
    recipeYield() {
      if (this.recipe?.yield) {
        const isObjectYield = isPlainObject(this.recipe?.yield);
        if (!isObjectYield) {
          const value = this.$get(this.recipe, 'yield', '');
          if (
            value?.toLowerCase().includes('serves') ||
            value?.toLowerCase().includes('makes')
          ) {
            return value;
          }

          return 'Serves ' + value;
        }
        if (isObjectYield && !this.$get(this.recipe, 'yield.title', '')) {
          return 'Serves ' + this.$get(this.recipe, 'yield.value', '');
        } else {
          return this.$get(this.recipe, 'yield.title', '').concat(
            ' ',
            this.$get(this.recipe, 'yield.value', '')
          );
        }
      }
      return null;
    },
    recipeTime() {
      const prepTime = this.$get(this.recipe, 'prepTime', '');
      const cookTime = this.$get(this.recipe, 'cookTime', '');
      const totalTime = this.$get(this.recipe, 'totalTime', '');

      const prepTimeString = prepTime ? 'Prep time ' + prepTime + '. ' : '';
      const cookTimeString = cookTime ? 'Cooking time ' + cookTime + '. ' : '';
      const totalTimeString = totalTime ? 'Total time ' + totalTime + '.' : '';

      return prepTimeString + cookTimeString + totalTimeString;
    },
    recipeIngredients() {
      let ingredients = this.$get(this.recipe, 'ingredients.elements', []);
      if (isEmpty(ingredients)) {
        ingredients = this.$get(this.recipe, 'ingredients', []);
      }

      if (Array.isArray(ingredients)) {
        const ingredientSections = ingredients.map((section) =>
          !isEmpty(section.elements) ? section.elements : section
        );
        return flatten(ingredientSections);
      } else {
        return [];
      }
    },
    recipeMethods() {
      let methods = this.$get(this.recipe, 'instructions.elements', []);
      if (isEmpty(methods)) {
        methods = this.$get(this.recipe, 'instructions', []);
      }

      if (Array.isArray(methods)) {
        const methodsSections = methods.map((section) =>
          !isEmpty(section.elements) ? section.elements : section
        );
        return flatten(methodsSections);
      } else {
        return [];
      }
    },
    previewImages() {
      return getPreviewImages(this.product);
    },
    isPreOrder() {
      return this.product?.availability === ProductAvailability.PreOrder;
    },
    recipeLinks() {
      const links = this.$get(this.recipe, 'links', []);
      if (links && links.length) {
        return groupBy(links, 'description');
      }
      return null;
    }
  },

  mounted() {
    if (
      this.$get(this.product, 'availability') !== productAvailability.PreOrder
    ) {
      fetchRecipesForOneProduct(this.$axios, this.product?.id).then(
        (recipes) => {
          this.recommendedRecipes = recipes?.data;
        }
      );
    }

    const { storeRegion, locale } = this.config || {};

    fetchProductsRecommended(this.$axios, this.product?.id, {
      query: { language: locale, country: storeRegion }
    })
      .then((products) => {
        this.bundleProduct = filterProductsByRegion(
          products?.data,
          this.region
        )?.[0];
      })
      .catch(() => {
        this.bundleProduct = null;
      });

    fetchProductConfig(this.$axios, this.product?.id).then((response) => {
      this.questions = this.$get(response, 'questions', []);
    });

    fetchCreatorsTrending(this.$axios).then((response) => {
      this.creators = response?.data;
    });

    fetchTrendingFeed(this.$axios).then((trendingFeed) => {
      this.trendingFeed = trendingFeed;
    });
  },

  methods: {
    handleProductBundleClick(product) {
      this.$router.push({
        path: `/products/${product?.id || product.item.id}`
      });
    },
    getLinkToCreator(creator) {
      return `/creators/${
        this.$get(creator, 'profile.handle') || this.$get(creator, 'id')
      }`;
    },
    handleCreatorClick(params) {
      this.$router.push({
        path: this.getLinkToCreator(params)
      });
    },
    handleRecipeClick(params) {
      this.$router.push({
        path: `/recipe/${this.$get(params, '_id')}`
      });
    },
    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },
    setQuantity(quantity) {
      this.quantity = quantity;
    },
    getImageItem(link) {
      const key = link.source;
      if (includes(SupportedCreatorPlatforms, key)) {
        return require(`@/assets/icons/creatorSocial/${key}.svg`);
      }
      return require('@/assets/icons/creatorSocial/link.png');
    },
    handleSocialIconClick(link) {
      window.open(link, '_blank');
    }
  }
};
</script>

<style scoped lang="scss">
.recipe {
  color: $white-color;
  overflow-x: hidden;

  &__author {
    font-weight: $font-weight-demi-bold;
    font-size: $font-size-large;

    a {
      overflow-wrap: anywhere;
      color: $primary-color;
    }
  }

  &__time,
  &__yield {
    font-weight: $font-weight-demi-bold;
  }

  &__icon {
    height: 30px;
    width: 30px;
  }

  h3 {
    font-weight: $font-weight-demi-bold;
  }

  &__ingredient {
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    color: $primary-color;
    font-weight: $font-weight-demi-bold;
  }

  &__method {
    &-order {
      display: inline-block;
      text-align: center;
      line-height: 30px;
      width: 30px;
      height: 30px;
      border: 1px solid $primary-color;
      border-radius: 50%;
      font-weight: $font-weight-medium-bold;
    }
  }

  &__links {
    &-title {
      text-align: center;
      font-weight: $font-weight-bold;
    }
    &-item {
      display: flex;
      align-items: center;
      background-color: $dark-grey-color;
      border-radius: 10px;
      padding: 16px 24px;
    }
  }
}
</style>
